// "Prices waiting for your OK" in Review (estimates.awaitingPriceOk, Q44): each estimate with option
// lines a technician priced below their limit, below cost or under the floor, who built it, and how
// far under the price list each line is; why it waits only where the server sends it (cost-seeing
// roles). OK an option's prices here (estimates.approvePrice) or open the job to look first.
// BL-189: a Section of RowList rows, an estimate a row (Open job its action), each option's lines and its
// OK these prices (a Link) a part of the row; what was just OK'd a Notice.

import { useEffect, useState } from 'react';
import { Link, Notice, RowList, type Row } from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { errorText } from '../books/ui.tsx';
import { openJob } from '../job/job-page.ts';
import {
  cutText,
  PRICE_OK_FIRST,
  PRICE_OK_MAX,
  signedWaitingText,
  waitingLineText,
  waitingMarginText,
  waitingSummary,
  type WaitingEstimate,
} from './price-ok-model.ts';

export function PriceOksSection({
  tick,
  timeZone,
  canOk,
}: {
  tick: number;
  timeZone: string;
  /** False while the company mirrors ServiceTitan: its estimates' prices are OK'd there. */
  canOk: boolean;
}) {
  const [es, setEs] = useState<WaitingEstimate[] | null>(null);
  const [total, setTotal] = useState(0);
  // "Show all" reads every one the server will give (it judges them all, so the total is true)
  const [limit, setLimit] = useState(PRICE_OK_FIRST);
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ estimates: WaitingEstimate[]; total: number }>('estimates.awaitingPriceOk', { limit })
      .then((r) => {
        setEs(r.estimates);
        setTotal(r.total);
      })
      .catch(() => setEs([]));
  }, [tick, again, limit]);
  // Nothing waits: the section stays out of the way.
  if (es === null || (es.length === 0 && !said)) return null;
  const cut = cutText(es.length, total);
  const ok = async (e: WaitingEstimate, optionId: string, title: string) => {
    setBusy(optionId);
    setSaid(null);
    try {
      await client.call('estimates.approvePrice', { estimateId: e.estimateId, optionId });
      setWhy(({ [optionId]: _, ...rest }) => rest);
      const signed = e.options.find((x) => x.optionId === optionId)?.signed;
      setSaid(
        signed
          ? `The prices on “${title}” (estimate ${e.number}) are OK’d, and the sale ${signed.signerName} signed went through.`
          : `The prices on “${title}” (estimate ${e.number}) are OK’d: it can be sent.`,
      );
      setAgain((n) => n + 1);
    } catch (err) {
      setWhy((w) => ({ ...w, [optionId]: errorText(err) }));
    } finally {
      setBusy(null);
    }
  };
  const row = (e: WaitingEstimate): Row => ({
    id: e.estimateId,
    name: `#${e.jobNumber} · ${e.customerName}`,
    actions: [
      { id: 'open', text: 'Open job', label: `Open job #${e.jobNumber}`, onPress: () => openJob(e.jobId) },
    ],
    lines: [
      { text: `Estimate ${e.number}: ${e.title}${e.techName ? ` · built by ${e.techName}` : ''}` },
      // each option: its lines under the price list, then its OK
      ...e.options.map((o) => ({
        part: (
          <div className="rv-ok" role="group" aria-label={o.title}>
            <b className="rv-ok__title">{o.title}</b>
            <ul className="rv-ok__lines" aria-label={`${o.title}: under the price list`}>
              {o.lines.map((l) => (
                <li key={l.lineId}>{waitingLineText(l)}</li>
              ))}
              {o.margin && <li>{waitingMarginText(o.margin)}</li>}
            </ul>
            {o.signed && <p className="rv-ok__signed">{signedWaitingText(o.signed, timeZone)}</p>}
            {canOk && (
              <span className="rv-ok__act">
                <Link
                  isDisabled={busy !== null}
                  label={`OK the prices on ${o.title}, estimate ${e.number}`}
                  onPress={() => void ok(e, o.optionId, o.title)}
                >
                  {busy === o.optionId ? 'OK’ing…' : 'OK these prices'}
                </Link>
              </span>
            )}
            {why[o.optionId] && (
              <p className="rv__bad" role="alert">
                {why[o.optionId]}
              </p>
            )}
          </div>
        ),
      })),
    ],
  });
  return (
    <Section title="Prices waiting for your OK">
      <div className="rv-oks">
        {said && <Notice tone="done" title={said} />}
        {es.length > 0 && <p className="rv__muted">{waitingSummary(es)}</p>}
        {/* the summary counts what's shown: past the first page, it says how many wait in all */}
        {cut && (
          <p className="rv__muted">
            {cut}{' '}
            {limit < PRICE_OK_MAX && (
              <Link onPress={() => setLimit(PRICE_OK_MAX)}>
                {total <= PRICE_OK_MAX ? `Show all ${total}` : `Show ${PRICE_OK_MAX}`}
              </Link>
            )}
          </p>
        )}
        {!canOk && <p className="rv__muted">{MIRROR_LINE}</p>}
        <RowList label="Prices waiting for your OK" rows={es.map(row)} />
      </div>
    </Section>
  );
}
